import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Kicker } from "@/components/ui/kicker";
import { Tonal } from "@/components/ui/tonal";
import type { ResolvedCitationData } from "@/components/citations";
import type { LivePhase } from "@/hooks/use-walkthrough-stream";
import type { LiveStepDisplay } from "./live-walkthrough-screenshot";
import { PHASE_LABELS, type UnifiedLiveFinding } from "./types";
import { GhostStep, HumanTestBlock, ErrorBlock } from "./live-timeline-blocks";
import { TimelineStepRow } from "./live-timeline-step";

interface LiveWalkthroughTimelineProps {
  steps: LiveStepDisplay[];
  activeStepIdx: number;
  onSelectStep: (idx: number) => void;
  findingsByStep: Map<number, UnifiedLiveFinding[]>;
  citationsMap: Map<string, ResolvedCitationData>;
  openPanel: (sourceType: string, sourceId: string) => void;
  isRunning: boolean;
  isComplete: boolean;
  isErrored: boolean;
  isCancelled: boolean;
  isLocalLive: boolean;
  isLive?: boolean;
  stepsBehindLive?: number;
  onResumeLive?: () => void;
  phase: LivePhase | null;
  errorMessage?: string | null;
  activeAction: {
    stepNumber: number;
    actionType: string;
    selector: string | null;
  } | null;
  completion: {
    terminationReason: string;
    verdict: { question: string; answer: string } | null;
    whatWorked: string[];
    openQuestions: string[];
  } | null;
  projectId: string;
  walkthroughId: string;
}

export function LiveWalkthroughTimeline({
  steps,
  activeStepIdx,
  onSelectStep,
  findingsByStep,
  citationsMap,
  openPanel,
  isRunning,
  isComplete,
  isErrored,
  isCancelled,
  isLocalLive,
  isLive = true,
  stepsBehindLive = 0,
  onResumeLive,
  phase,
  errorMessage,
  activeAction,
  completion,
  projectId,
  walkthroughId,
}: LiveWalkthroughTimelineProps) {
  const isBehindLive =
    !isLive && isRunning && !isComplete && !isErrored && stepsBehindLive > 0;
  const displayedStep = steps[activeStepIdx];

  return (
    <div className="space-y-10 px-7 py-9">
      <div className="flex items-baseline gap-3">
        <Kicker as="h2">
          {isComplete
            ? "Complete"
            : isErrored || isCancelled
              ? "Stopped"
              : "In progress"}
        </Kicker>
        <span className="text-sm tabular-nums text-muted-foreground">
          {steps.length === 0
            ? "Waiting on the first step…"
            : `${steps.length} step${steps.length !== 1 ? "s" : ""} observed`}
        </span>
      </div>

      {isBehindLive && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border/80 bg-surface-tonal px-4 py-3 text-xs">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="size-2 rounded-full bg-mark-helps animate-pulse"
            />
            <span className="font-semibold text-foreground">
              Viewing Step {displayedStep?.stepNumber ?? activeStepIdx + 1}
            </span>
            <span className="text-muted-foreground">
              ({stepsBehindLive} step{stepsBehindLive !== 1 ? "s" : ""} behind
              live)
            </span>
          </div>
          {onResumeLive && (
            <Button
              size="xs"
              variant="outline"
              onClick={onResumeLive}
              className="gap-1.5 bg-foreground text-background font-semibold shadow-xs hover:bg-foreground/90"
            >
              Go to Live →
            </Button>
          )}
        </div>
      )}

      {steps.length === 0 && !isErrored && (
        <Tonal padding="md" className="space-y-3">
          <div className="flex items-center gap-3">
            <RefreshCw
              className="h-3.5 w-3.5 animate-spin text-primary"
              aria-hidden="true"
            />
            <Kicker>Connecting</Kicker>
          </div>
          <p className="text-base text-foreground">
            {isLocalLive
              ? "Waiting for the walker's first step. Steps and findings appear here as the persona moves through the product."
              : "A runner is being assigned. Steps and findings will appear here as the persona navigates the product."}
          </p>
        </Tonal>
      )}

      {steps.length > 0 && (
        <ol className="space-y-9">
          {steps.map((step, idx) => {
            const isActive = idx === activeStepIdx;
            const stepFindings = findingsByStep.get(step.stepNumber) ?? [];
            const isLastStep = idx === steps.length - 1;
            const showWatching =
              isLastStep &&
              isRunning &&
              !isComplete &&
              !isErrored &&
              !activeAction &&
              (phase === null || phase === "walking");

            return (
              <TimelineStepRow
                key={step.id}
                stepNumber={step.stepNumber}
                title={step.title}
                personaReasoning={step.personaReasoning}
                grounding={step.grounding}
                findings={stepFindings}
                isActive={isActive}
                onSelect={() => onSelectStep(idx)}
                citationsMap={citationsMap}
                openPanel={openPanel}
                trailingSilentCue={showWatching}
              />
            );
          })}
          {activeAction && !isComplete && !isErrored && (
            <GhostStep
              stepNumber={activeAction.stepNumber}
              action={activeAction}
            />
          )}
        </ol>
      )}

      {isLocalLive &&
        phase &&
        phase !== "walking" &&
        isRunning &&
        !isComplete &&
        !isErrored && (
          <Tonal padding="md" className="space-y-2">
            <div className="flex items-center gap-3">
              <RefreshCw
                className="h-3.5 w-3.5 animate-spin text-primary"
                aria-hidden="true"
              />
              <Kicker>Walk finished</Kicker>
            </div>
            <p aria-live="polite" className="text-base text-foreground">
              {PHASE_LABELS[phase]}
            </p>
          </Tonal>
        )}

      {isCancelled && (
        <section className="space-y-4">
          <Kicker as="h2">Walkthrough stopped</Kicker>
          <div className="border-l-[3px] border-muted-foreground bg-surface-tonal px-5 py-4">
            <p className="text-base text-foreground">
              {isLocalLive
                ? "Stopped. The walker ends the run at its next step."
                : errorMessage || "Cancelled by user"}
            </p>
          </div>
        </section>
      )}

      {completion && (
        <HumanTestBlock
          completion={completion}
          projectId={projectId}
          walkthroughId={walkthroughId}
        />
      )}

      {isErrored && errorMessage && <ErrorBlock message={errorMessage} />}
    </div>
  );
}
